import React, { useState, useEffect } from 'react';
import { 
  Box, 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem, 
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import { getFirestore, collection, query, where, getDocs } from 'firebase/firestore';

const MembershipReports = () => {
  const [reportType, setReportType] = useState('');
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);

  const handleReportTypeChange = (event) => {
    setReportType(event.target.value);
  };

  const fetchMembers = async () => {
    setLoading(true);
    try {
      const db = getFirestore();
      let membersData = [];
      
      if (reportType === 'approved') {
        const membersRef = collection(db, "MembershipMaster");
        const q = query(membersRef, where("status", "==", "approved"));
        const querySnapshot = await getDocs(q);
        membersData = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
      } else {
        // For unapproved, get both pending and rejected memberships
        const requestsRef = collection(db, "MembershipRequests");
        const rejectedRef = collection(db, "RejectedMemberships");
        
        const [pendingSnapshot, rejectedSnapshot] = await Promise.all([
          getDocs(query(requestsRef, where("status", "==", "pending"))),
          getDocs(rejectedRef)
        ]);
        
        membersData = [
          ...pendingSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
            status: "pending"
          })),
          ...rejectedSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
            status: "rejected"
          }))
        ];
      }
      
      setMembers(membersData);
    } catch (error) {
      console.error("Error fetching members:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateReport = () => {
    if (reportType) {
      fetchMembers();
    }
  };

  const handleExportToCSV = () => {
    if (members.length === 0) return;

    const headers = [
      'First Name',
      'Surname',
      'Email',
      'Mobile Number',
      'Membership Category',
      'Status',
      'Status Date',
      'Date of Birth',
      'Residential Address 1',
      'Residential Address 2',
      'City',
      'State',
      'Country',
      'Professional Email',
      'Qualification',
      'Organization',
      'Number of Employees',
      'Designation',
      'Experience',
      'Expertise',
      'Payment Reference',
      'LinkedIn',
      'Facebook',
      'Instagram',
      'Referral Source',
      'Additional Comments',
      'Membership Start Date',
      'Membership End Date'
    ];

    const csvData = members.map(member => [
      member.firstName || '',
      member.surname || '',
      reportType === 'approved' ? member.professionalEmail : member.personalEmail || '',
      member.mobileNo || '',
      member.membershipCategory || '',
      member.status || '',
      reportType === 'approved' 
        ? (member.approvalDate ? new Date(member.approvalDate).toLocaleDateString() : 'N/A')
        : (member.status === 'rejected' 
            ? (member.rejectedDate ? new Date(member.rejectedDate).toLocaleDateString() : 'N/A')
            : 'Pending'),
      member.dob ? new Date(member.dob).toLocaleDateString() : '',
      member.resiAddress1 || '',
      member.resiAddress2 || '',
      member.city || '',
      member.state || '',
      member.country || '',
      member.professionalEmail || '',
      member.qualification || '',
      member.organisation || '',
      member.noOfEmployees || '',
      member.designation || '',
      member.experience || '',
      member.expertise || '',
      member.paymentRef || '',
      member.linkedIn || '',
      member.facebook || '',
      member.instagram || '',
      member.referralSource || '',
      member.additionalComments || '',
      member.membershipStartDate ? new Date(member.membershipStartDate).toLocaleDateString() : '',
      member.membershipEndDate ? new Date(member.membershipEndDate).toLocaleDateString() : ''
    ]);

    const csvContent = [headers, ...csvData]
      .map(row => row.join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `membership_report_${reportType}_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" sx={{ mb: 3 }}>
        Membership Reports
      </Typography>

      <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel>Report Type</InputLabel>
          <Select
            value={reportType}
            onChange={handleReportTypeChange}
            label="Report Type"
          >
            <MenuItem value="approved">Approved Memberships</MenuItem>
            <MenuItem value="unapproved">Unapproved Memberships</MenuItem>
          </Select>
        </FormControl>

        <Button 
          variant="contained" 
          onClick={handleGenerateReport}
          disabled={!reportType}
        >
          Generate Report
        </Button>

        <Button 
          variant="outlined" 
          onClick={handleExportToCSV}
          disabled={members.length === 0}
        >
          Export to CSV
        </Button>
      </Box>

      {loading ? (
        <Typography>Loading...</Typography>
      ) : members.length > 0 ? (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Mobile</TableCell>
                <TableCell>Membership Category</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>{reportType === 'approved' ? 'Approval Date' : 'Status Date'}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {members.map((member) => (
                <TableRow key={member.id}>
                  <TableCell>{`${member.firstName} ${member.surname}`}</TableCell>
                  <TableCell>{reportType === 'approved' ? member.professionalEmail : member.personalEmail}</TableCell>
                  <TableCell>{member.mobileNo}</TableCell>
                  <TableCell>{member.membershipCategory}</TableCell>
                  <TableCell>{member.status}</TableCell>
                  <TableCell>
                    {reportType === 'approved' 
                      ? member.approvalDate 
                        ? new Date(member.approvalDate).toLocaleDateString() 
                        : 'N/A'
                      : member.status === 'rejected'
                        ? member.rejectedDate 
                          ? new Date(member.rejectedDate).toLocaleDateString() 
                          : 'N/A'
                        : 'Pending'
                    }
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Typography>No data to display. Please select a report type and generate the report.</Typography>
      )}
    </Box>
  );
};

export default MembershipReports; 